/**
 * widgetSurfacePreferencesApi.ts — [T246.5-FE-HOOKS]
 *
 * 4 TanStack Query v5 hooks that bind to the BE `/widgets` surface-prefs
 * endpoints (T246.5-BE-ENDPOINTS). The hooks form the lowest-level data
 * layer for the `/widgets/insights` curation page (T246.5-FE-PAGE) and the
 * cross-surface `TransactionInsightsCard` rewrite (T246.5-FE-WIRE).
 *
 * Hook surface (the contract for AC2–AC5):
 *   1. `useGetWidgetSurfacePreferences(surface?)`
 *      GET hook.
 *         - `queryKey: ['widget-surface-prefs', surface ?? 'all']`
 *         - `staleTime: WIDGET_SURFACE_PREF_STALE_TIME_MS` (5 min)
 *      Returns the per-surface entry when `surface` is provided, or the
 *      all-surfaces map when omitted. Both shapes share the same
 *      `queryKey` namespace so a future `invalidateQueries` call can
 *      target either slot with a prefix-aware filter.
 *
 *   2. `useToggleWidgetSurfacePreference()`
 *      POST `/widgets/toggle` mutation hook.
 *      Optimistic update flow:
 *         - `onMutate`: cancel in-flight queries for both the single-surface
 *           key and the all-surfaces key, snapshot previous cache, flip
 *           the relevant `enabled` bit, write the optimistic value back.
 *         - `onError`: restore the snapshot for both keys.
 *         - `onSettled`: invalidate BOTH keys (success or error) so the
 *           server's authoritative state replaces the optimistic value
 *           on the next tick. This is the R1 mitigation (PRD §13.5):
 *           rollback restores the previous SERVER state, not the pre-toggle
 *           state, and per-mutation settled invalidation is sequential so
 *           a rapid 3-toggle race does not cascade.
 *
 *   3. `useReplaceWidgetSurfacePreferences(surface)`
 *      PATCH `/widgets?surface=X` mutation hook for bulk reorder.
 *      Optimistic update writes the full replacement `enabled` list,
 *      rollback restores the snapshot, settled invalidates only the
 *      affected surface key (atomic replace means no other surfaces can
 *      have shifted).
 *
 *   4. `useResetWidgetSurfacePreferences(surface)`
 *      Reset-to-defaults mutation hook. Reads the cached `default` list
 *      from the GET cache (it is always present because the BE returns
 *      it on every response), then delegates to the PATCH mutation via
 *      `useReplaceWidgetSurfacePreferences`. Falls back to an empty PATCH
 *      body if the cache is cold — in that case the BE will reject the
 *      empty list (min-length validation), and the user sees a clean
 *      error in the toast layer rather than a silent reset to nothing.
 *
 * TanStack Query v5 docs: https://tanstack.com/query/latest
 *
 * @module features/dashboard/widgetSurfacePreferencesApi
 * @see .omo/plans/T246-PY-insights-graph-revamp-prd.md §13.4 (FE-HOOKS)
 *      + §13.5 (R1–R8 risk register)
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import api from '@/services/api';
import {
  WIDGET_SURFACE_PREF_KEY_PREFIX,
  WIDGET_SURFACE_PREF_STALE_TIME_MS,
  type ToggleWidgetInput,
  type WidgetSurfacePreferences,
  type WidgetSurfacePreferenceEntry,
} from '@/lib/widgetSurfacePreferences';

// ── Endpoint paths ────────────────────────────────────────────────────────

/**
 * Canonical endpoint paths. Kept inline (not via `endpoints.X`) because
 * the BE-ENDPOINTS surface landed under `/users/me/preferences/widgets/...`
 * which is not yet part of `@/services/api`'s endpoints catalogue — added
 * here so the consumer can flip to a switch to `endpoints.X` once the BE
 * PR lands and the catalogue update ships.
 */
const PREFERENCES_WIDGETS_ALL_PATH = '/users/me/preferences/widgets';

const preferencesWidgetsSurfacePath = (surface: string): string =>
  `${PREFERENCES_WIDGETS_ALL_PATH}?surface=${encodeURIComponent(surface)}`;

const PREFERENCES_WIDGETS_TOGGLE_PATH =
  '/users/me/preferences/widgets/toggle';

// ── BE response envelopes ─────────────────────────────────────────────────

interface WidgetSurfaceToggleResponse {
  success: boolean;
  surface: string;
  kind: string;
  enabled: boolean;
  changed: boolean;
  enabledList: string[];
  available: string[];
  default: string[];
  updatedAt: string | null;
}

interface WidgetSurfaceReplaceResponse {
  success: boolean;
  surface: string;
  enabled: string[];
  available: string[];
  default: string[];
  updatedAt: string | null;
  changedCount: number;
}

interface WidgetSurfaceAllResponse {
  success: boolean;
  surfaces: WidgetSurfacePreferences;
  migrated: boolean;
  migratedCount?: number;
}

interface WidgetSurfaceSingleResponse extends WidgetSurfacePreferenceEntry {
  success: boolean;
}

// ── queryFn implementations ────────────────────────────────────────────────

/**
 * `axios.get<T>` unwraps `.data` from the response envelope. The hooks
 * expose `.data` as a TanStack Query `data` field, and the components
 * consume it via `result.data` directly. We co-locate the fetch logic
 * here so both GET hooks can share it without circular imports.
 */
async function fetchAllWidgetSurfacePreferences(): Promise<WidgetSurfacePreferences> {
  const response = await api.get<WidgetSurfaceAllResponse>(
    PREFERENCES_WIDGETS_ALL_PATH
  );
  return response.data.surfaces;
}

async function fetchSingleWidgetSurfacePreference(
  surface: string
): Promise<WidgetSurfacePreferenceEntry> {
  const response = await api.get<WidgetSurfaceSingleResponse>(
    preferencesWidgetsSurfacePath(surface)
  );
  // The BE envelopes every response in `{ success, ...entry }`. We drop
  // `success` from the returned entry shape (it is a wire-protocol
  // concern, not a domain concern) by spreading the data and excluding
  // the boolean manually — destructuring rest with a leading-underscore
  // linter pattern would also work but ESLint's
  // `@typescript-eslint/no-unused-vars` flags the binder.
  const data: WidgetSurfaceSingleResponse = response.data;
  const entry: WidgetSurfacePreferenceEntry = {
    surface: data.surface,
    enabled: data.enabled,
    available: data.available,
    default: data.default,
    updatedAt: data.updatedAt,
  };
  return entry;
}

async function postToggleWidget(
  input: ToggleWidgetInput
): Promise<WidgetSurfaceToggleResponse> {
  const response = await api.post<WidgetSurfaceToggleResponse>(
    PREFERENCES_WIDGETS_TOGGLE_PATH,
    input
  );
  return response.data;
}

async function patchReplaceWidgetSurface(params: {
  surface: string;
  enabled: string[];
}): Promise<WidgetSurfaceReplaceResponse> {
  const { surface, enabled } = params;
  const response = await api.patch<WidgetSurfaceReplaceResponse>(
    preferencesWidgetsSurfacePath(surface),
    { enabled }
  );
  return response.data;
}

// ── Hook 1: GET ───────────────────────────────────────────────────────────

/**
 * GET hook for a single surface (or all surfaces when `surface` is omitted).
 *
 * Result types are intentionally a union so consumers can branch:
 *   - `useGetWidgetSurfacePreferences('transactions')` → returns
 *     `WidgetSurfacePreferenceEntry | undefined`
 *   - `useGetWidgetSurfacePreferences()` → returns
 *     `WidgetSurfacePreferences | undefined`
 *
 * An empty-string `surface` is normalised to the `'all'` cache slot via
 * the `surface.length > 0` guard so uninitialised form state never
 * produces a phantom per-empty-string cache entry.
 */
export function useGetWidgetSurfacePreferences(
  surface?: string
): UseQueryResult<
  WidgetSurfacePreferenceEntry | WidgetSurfacePreferences,
  Error
> {
  const queryKey: [string, string] =
    surface && surface.length > 0
      ? [WIDGET_SURFACE_PREF_KEY_PREFIX, surface]
      : [WIDGET_SURFACE_PREF_KEY_PREFIX, 'all'];

  return useQuery<
    WidgetSurfacePreferenceEntry | WidgetSurfacePreferences,
    Error
  >({
    queryKey,
    queryFn: () =>
      surface && surface.length > 0
        ? fetchSingleWidgetSurfacePreference(surface)
        : fetchAllWidgetSurfacePreferences(),
    staleTime: WIDGET_SURFACE_PREF_STALE_TIME_MS,
    // Slightly higher retry budget than the client default: a transient
    // 5xx from the BE (M0 Atlas restarts) is recoverable and the user is
    // staring at the curation UI waiting for content.
    retry: 2,
  });
}

// ── Mutation contexts (rollback snapshot handle) ───────────────────────────

/**
 * Returned by `onMutate` and consumed by `onError` for snapshot rollback.
 * Holds the pre-toggle shape for BOTH the per-surface key and the all-
 * surfaces key, so a rollback restores the cached UI in both views
 * without a second round-trip.
 */
interface ToggleSnapshot {
  surface: string;
  previousSingle: WidgetSurfacePreferenceEntry | undefined;
  previousAll: WidgetSurfacePreferences | undefined;
}

/**
 * Same as `ToggleSnapshot`, but for the replace / reset path.
 * The replace path does NOT snapshot the all-surfaces key because the
 * replace is per-surface atomic; only the single-surface cache slot can
 * have shifted.
 */
interface ReplaceSnapshot {
  surface: string;
  previousSingle: WidgetSurfacePreferenceEntry | undefined;
}

// ── Hook 2: POST /widgets/toggle ──────────────────────────────────────────

/**
 * Single-toggle mutation hook. Adds or removes one widget kind from a
 * surface's `enabled` list.
 *
 * Optimitic flow (PRD §13.5 R1):
 *   1. Cancel in-flight queries for both key slots so the optimistic
 *      write doesn't get clobbered by a stale refetch.
 *   2. Snapshot previous cache for both slots.
 *   3. Compute the optimistic next state (flip one bit).
 *   4. `setQueryData` for both slots with the optimistic value.
 *   5. On error, restore both snapshots (parallel — TanStack serialises
 *      setters internally so no race).
 *   6. On settled (success OR error), invalidate BOTH slots so the
 *      server's authoritative state replaces the optimistic value.
 */
export function useToggleWidgetSurfacePreference(): UseMutationResult<
  WidgetSurfaceToggleResponse,
  Error,
  ToggleWidgetInput,
  ToggleSnapshot
> {
  const queryClient = useQueryClient();

  return useMutation<
    WidgetSurfaceToggleResponse,
    Error,
    ToggleWidgetInput,
    ToggleSnapshot
  >({
    mutationFn: (input) => postToggleWidget(input),

    onMutate: async (input) => {
      // Cancel any in-flight reads for both slots so they don't overwrite
      // the optimistic value mid-mutation.
      await queryClient.cancelQueries({
        queryKey: [WIDGET_SURFACE_PREF_KEY_PREFIX],
      });

      // The toggle endpoint derives the surface server-side. We mirror
      // that derivation here by reading the cached all-surfaces map and
      // picking the first curated surface — falling back to the
      // canonical 'transactions' surface if the cache is cold.
      const previousAll = queryClient.getQueryData<WidgetSurfacePreferences>([
        WIDGET_SURFACE_PREF_KEY_PREFIX,
        'all',
      ]);
      const firstSurface =
        previousAll && Object.keys(previousAll).length > 0
          ? Object.keys(previousAll)[0] ?? 'transactions'
          : 'transactions';
      const singleKey: [string, string] = [
        WIDGET_SURFACE_PREF_KEY_PREFIX,
        firstSurface,
      ];
      const previousSingle =
        queryClient.getQueryData<WidgetSurfacePreferenceEntry>(singleKey);

      // Optimistic write — flip the bit in the single-surface entry.
      if (previousSingle) {
        const nextEnabled = input.enabled
          ? previousSingle.enabled.includes(input.kind)
            ? previousSingle.enabled
            : [...previousSingle.enabled, input.kind]
          : previousSingle.enabled.filter((k) => k !== input.kind);
        const optimistic: WidgetSurfacePreferenceEntry = {
          ...previousSingle,
          enabled: nextEnabled,
          updatedAt: new Date().toISOString(),
        };
        queryClient.setQueryData(singleKey, optimistic);
      }

      // Optimistic write — mirror in the all-surfaces map if present.
      if (previousAll) {
        const prevEntry = previousAll[firstSurface];
        if (prevEntry) {
          const nextEnabled = input.enabled
            ? prevEntry.enabled.includes(input.kind)
              ? prevEntry.enabled
              : [...prevEntry.enabled, input.kind]
            : prevEntry.enabled.filter((k) => k !== input.kind);
          const updatedAll: WidgetSurfacePreferences = {
            ...previousAll,
            [firstSurface]: {
              ...prevEntry,
              enabled: nextEnabled,
              updatedAt: new Date().toISOString(),
            },
          };
          queryClient.setQueryData(
            [WIDGET_SURFACE_PREF_KEY_PREFIX, 'all'],
            updatedAll
          );
        }
      }

      return {
        surface: firstSurface,
        previousSingle,
        previousAll,
      };
    },

    onError: (_err, _input, snapshot) => {
      if (!snapshot) return;
      const surfaceKey: [string, string] = [
        WIDGET_SURFACE_PREF_KEY_PREFIX,
        snapshot.surface,
      ];
      // Rollback restores the previous server snapshot (NOT the pre-toggle
      // state — that's what R1 specifically forbids).
      if (snapshot.previousSingle !== undefined) {
        queryClient.setQueryData(surfaceKey, snapshot.previousSingle);
      }
      if (snapshot.previousAll !== undefined) {
        queryClient.setQueryData(
          [WIDGET_SURFACE_PREF_KEY_PREFIX, 'all'],
          snapshot.previousAll
        );
      }
    },

    onSettled: () => {
      // Invalidate BOTH slots so the authoritative server state replaces
      // the optimistic value regardless of success or error. TanStack
      // invalidations on the same prefix queue sequentially — no cascade.
      return queryClient.invalidateQueries({
        queryKey: [WIDGET_SURFACE_PREF_KEY_PREFIX],
      });
    },
  });
}

// ── Hook 3: PATCH /widgets?surface=X (replace) ────────────────────────────

/**
 * Bulk-replace mutation hook. Used by the drag-reorder interaction
 * (T246.5-FE-PAGE AC3) and as the inner engine of `useReset*`.
 *
 * Optimistic write replaces the per-surface `enabled` list in-place.
 * Rollback restores the prior list. Settled invalidates only the
 * affected per-surface key — atomic per-surface replace means the
 * all-surfaces view can only differ in one entry, and invalidating it
 * is cheap.
 */
export function useReplaceWidgetSurfacePreferences(
  surface: string
): UseMutationResult<
  WidgetSurfaceReplaceResponse,
  Error,
  { enabled: string[] },
  ReplaceSnapshot
> {
  const queryClient = useQueryClient();
  const surfaceKey: [string, string] = [
    WIDGET_SURFACE_PREF_KEY_PREFIX,
    surface,
  ];

  return useMutation<
    WidgetSurfaceReplaceResponse,
    Error,
    { enabled: string[] },
    ReplaceSnapshot
  >({
    mutationFn: (vars) =>
      patchReplaceWidgetSurface({ surface, enabled: vars.enabled }),

    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: surfaceKey });
      const previousSingle =
        queryClient.getQueryData<WidgetSurfacePreferenceEntry>(surfaceKey);

      if (previousSingle) {
        const optimistic: WidgetSurfacePreferenceEntry = {
          ...previousSingle,
          enabled: vars.enabled,
          updatedAt: new Date().toISOString(),
        };
        queryClient.setQueryData(surfaceKey, optimistic);
      }

      // Also mirror to the all-surfaces cache so a second consumer
      // (e.g. the Wave 6 page header) doesn't desync.
      const previousAll = queryClient.getQueryData<WidgetSurfacePreferences>([
        WIDGET_SURFACE_PREF_KEY_PREFIX,
        'all',
      ]);
      if (previousAll && previousAll[surface]) {
        const updatedAll: WidgetSurfacePreferences = {
          ...previousAll,
          [surface]: {
            ...previousAll[surface],
            enabled: vars.enabled,
            updatedAt: new Date().toISOString(),
          },
        };
        queryClient.setQueryData(
          [WIDGET_SURFACE_PREF_KEY_PREFIX, 'all'],
          updatedAll
        );
      }

      return { surface, previousSingle };
    },

    onError: (_err, _vars, snapshot) => {
      if (!snapshot) return;
      if (snapshot.previousSingle !== undefined) {
        queryClient.setQueryData(surfaceKey, snapshot.previousSingle);
      }
    },

    onSettled: () => {
      return queryClient.invalidateQueries({ queryKey: surfaceKey });
    },
  });
}

// ── Hook 4: PATCH /widgets?surface=X (reset-to-defaults wrapper) ───────────

export interface UseResetWidgetSurfacePreferencesResult {
  /** The default-list (read from the GET cache if available). */
  defaults: string[] | undefined;
  /**
   * Trigger function. Returns a promise that resolves only when the BE
   * PATCH succeeds; rejects on validation failure or network error.
   */
  reset: (variables?: { defaults?: string[] }) => Promise<unknown>;
  /** Aggregated mutation state, identical shape to `useReplace*`. */
  isUpdating: boolean;
  error: Error | null;
}

/**
 * Reset-to-defaults mutation hook.
 *
 * Strategy: read the cached single-surface entry. If `default` is present,
 * delegate to the PATCH mutation. Otherwise throw a deterministic Error —
 * the consumer is expected to ensure the GET hook has populated the cache
 * before invoking `reset()`.
 *
 * The wrapper REUSES the underlying replace mutation flow so optimistic
 * update, rollback, and settled invalidation are all inherited.
 */
export function useResetWidgetSurfacePreferences(
  surface: string
): UseResetWidgetSurfacePreferencesResult {
  const queryClient = useQueryClient();
  const surfaceKey: [string, string] = [
    WIDGET_SURFACE_PREF_KEY_PREFIX,
    surface,
  ];

  const mutation = useMutation<
    WidgetSurfaceReplaceResponse,
    Error,
    { enabled: string[] },
    ReplaceSnapshot
  >({
    mutationFn: (vars) =>
      patchReplaceWidgetSurface({ surface, enabled: vars.enabled }),

    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: surfaceKey });
      const previousSingle =
        queryClient.getQueryData<WidgetSurfacePreferenceEntry>(surfaceKey);

      if (previousSingle) {
        const optimistic: WidgetSurfacePreferenceEntry = {
          ...previousSingle,
          enabled: vars.enabled,
          updatedAt: new Date().toISOString(),
        };
        queryClient.setQueryData(surfaceKey, optimistic);
      }

      return { surface, previousSingle };
    },

    onError: (_err, _vars, snapshot) => {
      if (!snapshot) return;
      if (snapshot.previousSingle !== undefined) {
        queryClient.setQueryData(surfaceKey, snapshot.previousSingle);
      }
    },

    onSettled: () => {
      return queryClient.invalidateQueries({
        queryKey: [WIDGET_SURFACE_PREF_KEY_PREFIX],
      });
    },
  });

  /**
   * Read the cached entry to discover the `default` list. We poll on
   * mount only — subsequent reads are picked up via the `invalidateQueries`
   * call after the reset mutation settles.
   */
  const cached = queryClient.getQueryData<WidgetSurfacePreferenceEntry>(
    surfaceKey
  );
  const defaults = cached?.default;

  const reset = async (variables?: { defaults?: string[] }) => {
    const nextDefaults = variables?.defaults ?? defaults ?? [];
    if (nextDefaults.length === 0) {
      // The BE rejects an empty enabled list (min 1). Surface the
      // error early so the caller can show a toast instead of an
      // opaque 400.
      throw new Error(
        `[useResetWidgetSurfacePreferences] No defaults available for surface "${surface}" — GET must populate the cache first.`
      );
    }
    return mutation.mutateAsync({ enabled: nextDefaults });
  };

  return {
    defaults,
    reset,
    isUpdating: mutation.isPending,
    error: mutation.error,
  };
}
